import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { vsprintf } from "sprintf-js";
import { useDispatch } from 'react-redux';
import { setInventoryCodeItem } from '../../../utils/store/action/varValueActions';


const ColumnItem = ({
  params,
  item,
  handleRowUpdate,
  colItemOptions = [],
  rowError
}) => {
  const displayField = item?.inputType?.data?.sDisplayField || '';
  const displayFormat = item?.inputType?.data?.sDisplayFormat || '';
  const [searchedText, setSearchedText] = useState("");
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();


  

  const processedOptions = useMemo(() => {
    return colItemOptions.map(opt => {
      const keys = [...(displayField.matchAll(/{(.*?)}/g) || [])].map(m => m[1]);
      const args = keys.map(k => {
        const v = opt[k];
        return v === undefined || v === null ? "" : String(v);
      });
      let _display = "";
      try {
        _display = vsprintf(displayFormat ?? "%s", args);
      } catch (e) {
        _display = args.join(", ");
      }

      return { ...opt, _display };
    });
  }, [colItemOptions, displayField, displayFormat]);

  const isMultiple = item?.inputType?.component?.options?.mode === "MULTIVALUE";
  const valueField = item?.inputType?.data?.sValueField;

  const selectedValue = useMemo(() => {
    const rowVal = params?.row?.[item?.sColumnID];

    if (isMultiple) {
      if (!rowVal) return [];
      if (Array.isArray(rowVal)) {
        return processedOptions.filter(opt => rowVal.includes(opt?.[valueField]));
      }
      if (typeof rowVal === 'string') {
        const parts = rowVal.split(',').map(p => p.trim()).filter(Boolean);
        return processedOptions.filter(opt => parts.includes(String(opt?.[valueField])));
      }
      const single = processedOptions.find(opt => opt?.[valueField] === rowVal);
      return single ? [single] : [];
    } else {
      if (rowVal == null) return null;
      return processedOptions.find(opt => opt?.[valueField] === rowVal) || null;
    }
  }, [params?.row, item?.sColumnID, isMultiple, processedOptions, valueField]);

  const [internalValue, setInternalValue] = useState(
    () => (isMultiple ? (selectedValue || []) : (selectedValue || null))
  );

  useEffect(() => {
    if (!open) {
      setInternalValue(isMultiple ? (selectedValue || []) : (selectedValue || null));
    }
  }, [selectedValue, open]);

  // Add this inside your component
useEffect(() => {
  dispatch(setInventoryCodeItem(internalValue?.sInventoryCode))
}, [internalValue]);

  const filterOptions = useCallback((options, state) => {
    const search = (state.inputValue || "").toLowerCase();
    return options.filter(opt =>
      (opt._display || '').toLowerCase().includes(search)
    );
  }, []);

  const handleItemChange = async (e, newValue) => {
    setInternalValue(newValue);

    if (!newValue) {
      handleRowUpdate({
        fieldName: item?.sColumnID,
        value: isMultiple ? [] : null,
        rowId: params?.row?.id,
        colItemData: isMultiple ? [] : null
      });
      return;
    }

    if (isMultiple) {
      const valuesArray = Array.isArray(newValue) ? newValue.map(n => n?.[valueField]) : [];
      handleRowUpdate({
        fieldName: item?.sColumnID,
        value: valuesArray,
        rowId: params?.row?.id,
        colItemData: newValue
      });
      return;
    }

    const selectedOption = newValue;
    if (!selectedOption || selectedOption === "") {
      handleRowUpdate({ fieldName: item?.sColumnID, value: null, rowId: params?.row?.id, colItemData: null });
      return;
    }

    // Commit inventory immediately so quantity/UOM loading is never blocked by
    // pricing I/O. InputTableDefault's guarded pricing effect updates the rate.
    handleRowUpdate({
      fieldName: item?.sColumnID,
      value: selectedOption?.[valueField],
      rowId: params?.row?.id,
      colItemData: selectedOption
    });
  };

  // Use backend field name from _fieldIds mapping if available, otherwise fall back to sName
  const fieldId = params?.row?._fieldIds?.[item?.sColumnID] || item?.inputType?.component?.sName;

  return (
  <Autocomplete
    options={processedOptions}
    name={item?.inputType?.component?.sName}
    multiple={isMultiple}
    id={`${fieldId}-${params?.row?.id}`}
    value={internalValue}
    onChange={handleItemChange}
    getOptionLabel={(option) => option?._display || ''}
    isOptionEqualToValue={(option, value) =>
      option?.[valueField] === value?.[valueField]
    }
    onInputChange={(e, newInputValue) => {
      if (typeof newInputValue === "string") {
        setSearchedText(newInputValue);
      }
    }}
    onOpen={() => {
      setOpen(true);
      // setInternalValue(isMultiple ? [] : null);
    }}
    onClose={() => {
      setOpen(false);
      setInternalValue(isMultiple ? (selectedValue || []) : (selectedValue || null));
    }}
    renderInput={(params) => {
      const { inputProps, ...restParams } = params;
      const { onKeyDown: originalOnKeyDown, value: inputValueFromParams, ...restInputProps } = inputProps || {};

      const wrappedInputProps = {
        ...restInputProps,
        value: typeof inputValueFromParams === 'string'
          ? inputValueFromParams.replace(/<[^>]+>/g, "")
          : inputValueFromParams,
        onKeyDown: (e) => {
          if (typeof originalOnKeyDown === 'function') {
            originalOnKeyDown(e);
          }

          const key = e.key || '';
          const code = e.code || '';
          const isSpace = key === ' ' || key === 'Spacebar' || code === 'Space';
          if (isSpace) {
            e.stopPropagation();
          }
        }
      };

      return (
        <TextField
          {...restParams}
          inputProps={wrappedInputProps}
          placeholder="Select Item"
          error={!!rowError}
          helperText={rowError || ""}
          size="medium"
        />
      );
    }}
    clearIcon={<CloseIcon fontSize="small" />}
    filterOptions={filterOptions}
    {...item?.inputType?.component?.sProps}
  />
);
};

export default ColumnItem;
